"use client";

import React from "react";
import { Lock, Check } from "lucide-react";
import { DialogTitle, DialogDescription } from "@/app/_module/components/ui/dialog";

interface ComingSoonPanelProps {
  title: string;
  message: string;
  /** Render the heading/description as Radix Dialog primitives (a11y) when inside a Dialog. */
  asDialog?: boolean;
  /** The feature's own icon (defaults to a lock). */
  icon?: React.ElementType;
  /** Optional bullet list of what the feature offers. */
  features?: string[];
  /** Optional plan note, e.g. "Included with all subscriptions". */
  planNote?: string;
  /** Show the "Coming Soon" status pill (default true). Turn off for the upgrade-teaser variant. */
  showStatus?: boolean;
  /** Optional action shown below (e.g. a "Return" or "Upgrade" button). */
  action?: React.ReactNode;
}

/**
 * On-brand "Coming Soon" panel for locked features. Matches the InsuraCentral
 * theme: white card, brand-blue (`--primary`) accents, the app's Fraunces
 * display serif for the headline, Geist body. Shared by the sidebar popup and
 * the full-page route gate so they stay identical.
 */
export function ComingSoonPanel({
  title,
  message,
  asDialog = false,
  icon: Icon = Lock,
  features,
  planNote,
  showStatus = true,
  action,
}: ComingSoonPanelProps) {
  const Heading: any = asDialog ? DialogTitle : "h1";
  const Body: any = asDialog ? DialogDescription : "p";

  return (
    <div className="relative overflow-hidden bg-white">
      {/* soft brand wash from the top + a hairline in the brand color */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-primary/[0.08] to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
      />

      <div className="relative px-8 py-9 text-center">
        {/* feature icon, in the brand blue */}
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-1 ring-primary/15">
          <Icon className="h-8 w-8" />
        </div>

        <Heading
          className="text-[1.7rem] font-medium leading-tight tracking-tight text-foreground"
          style={{ fontFamily: "var(--font-fraunces), Georgia, serif" }}
        >
          {title}
        </Heading>

        <Body className="mx-auto mt-2.5 max-w-sm text-sm leading-relaxed text-muted-foreground">
          {message}
        </Body>

        {planNote ? (
          <div className="mt-3 flex justify-center">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-600/15">
              <Check className="h-3.5 w-3.5" strokeWidth={2.75} />
              {planNote}
            </span>
          </div>
        ) : null}

        {features && features.length > 0 ? (
          <div className="mx-auto mt-6 max-w-[18rem] rounded-xl border border-gray-100 bg-gray-50/70 p-4 text-left">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              What you'll get
            </p>
            <ul className="space-y-2">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13px] leading-snug text-gray-700">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" strokeWidth={2.5} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {showStatus ? (
          <p className="mt-5 text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            Coming Soon
          </p>
        ) : null}

        {action ? <div className="mt-7 flex justify-center">{action}</div> : null}
      </div>
    </div>
  );
}
